import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from "@simplewebauthn/browser";
import { ApiError, type User } from "./api";

/**
 * Passkeys: the primary way in.
 *
 * The private key never leaves the device and is cryptographically bound to the
 * domain, so a spoofed site cannot ask it to authenticate. That is why the
 * adversary-in-the-middle attack -- which does work against an emailed code -- is
 * mitiga: es imposible.
 *
 * The challenge id travels in an httpOnly cookie the server sets, not in the
 * body: the browser that asked for the challenge is the only one that can answer.
 */

export interface SavedPasskey {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  /** Si el autenticador la respalda (iCloud, Google): perder el aparato no la pierde. */
  backedUp: boolean;
}

const BASE = "/api/v1";

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(BASE + path, { credentials: "include", ...init });
  } catch {
    throw new ApiError("No pudimos conectar con el servidor.", 0);
  }
  if (res.ok) return res;

  let message = `Something went wrong (${res.status}).`;
  try {
    const body = (await res.clone().json()) as { error?: unknown };
    if (typeof body.error === "string" && body.error) message = body.error;
  } catch {
    /* el cuerpo no era JSON */
  }
  throw new ApiError(message, res.status);
}

/** Can this device use passkeys with biometrics? */
export async function isAvailable(): Promise<boolean> {
  if (!browserSupportsWebAuthn()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
}

/**
 * Maps browser errors onto codes the UI can translate.
 *
 * The standard's own are cryptic -- `NotAllowedError` covers everything from
 * "cancelled" to "timed out" -- and showing them raw helps nobody. The message
 * is English and only a fallback; `code` is what gets localised.
 */
function friendly(err: unknown, context: "register" | "login"): ApiError {
  if (err instanceof ApiError) return err;
  const registering = context === "register";
  const name = err instanceof Error ? err.name : "";
  switch (name) {
    case "NotAllowedError":
      return registering
        ? new ApiError("Registration was cancelled or timed out.", 0, "passkey_register_cancelled")
        : new ApiError("Sign-in was cancelled or timed out.", 0, "passkey_login_cancelled");
    case "InvalidStateError":
      return new ApiError(
        "This device is already registered on your account.", 0, "passkey_already_registered");
    case "NotSupportedError":
      return new ApiError("This device cannot use passkeys.", 0, "passkey_unsupported");
    case "SecurityError":
      return new ApiError("This site is not secure enough for passkeys.", 0, "passkey_insecure");
    default:
      return registering
        ? new ApiError("We could not register this device.", 0, "passkey_register_failed")
        : new ApiError("We could not sign you in with that device.", 0, "passkey_login_failed");
  }
}

/** Registers this device on the account already signed in. */
export async function register(label: string): Promise<void> {
  try {
    const options = await (await call("/passkeys/register/begin", { method: "POST" })).json();
    const attestation = await startRegistration({ optionsJSON: options.publicKey ?? options });
    await call(`/passkeys/register/finish?label=${encodeURIComponent(label)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(attestation),
    });
  } catch (err) {
    throw friendly(err, "register");
  }
}

/**
 * Entra con passkey SIN escribir el correo.
 *
 * The device knows who each credential belongs to, so the person only touches the
 * sensor. As a bonus, it reveals nothing about which accounts exist.
 */
export async function login(): Promise<User> {
  try {
    const options = await (await call("/auth/passkey/login/begin", { method: "POST" })).json();
    const assertion = await startAuthentication({ optionsJSON: options.publicKey ?? options });
    const res = await call("/auth/passkey/login/finish", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(assertion),
    });
    return (await res.json()) as User;
  } catch (err) {
    throw friendly(err, "login");
  }
}

export async function list(): Promise<SavedPasskey[]> {
  return (await (await call("/passkeys")).json()) as SavedPasskey[];
}

export async function remove(id: string): Promise<void> {
  await call(`/passkeys/${encodeURIComponent(id)}`, { method: "DELETE" });
}
